import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, TextField, Button, CircularProgress } from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import AdminShell from "../layout/AdminShell";
import { adminGetBusinessPageContent, adminUpdateBusinessPageContent } from "../../api/business";

export default function PageContentPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-business-page-content"],
    queryFn: () => adminGetBusinessPageContent().then((r) => r.data),
  });

  useEffect(() => {
    if (data) {
      setForm({
        ...data,
        // scope_cards comes back as a JSON array; edit it as raw JSON text here
        scope_cards: typeof data.scope_cards === "string" ? data.scope_cards : JSON.stringify(data.scope_cards ?? [], null, 2),
      });
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => adminUpdateBusinessPageContent(form),
    onSuccess: () => { qc.invalidateQueries(["admin-business-page-content"]); toast.success("Page content updated"); },
    onError: (err) => toast.error(err?.response?.data ? JSON.stringify(err.response.data) : "Save failed"),
  });

  if (isLoading || !form) {
    return (
      <AdminShell title="Business Page Content">
        <Box display="flex" justifyContent="center" mt={10}><CircularProgress /></Box>
      </AdminShell>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <AdminShell title="Business Page Content">
      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
        <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2, maxWidth: 640 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Hero Section</Typography>
          <TextField size="small" label="Hero Title" value={form.hero_title ?? ""} onChange={(e) => set("hero_title", e.target.value)} />
          <TextField size="small" label="Hero Subtitle" multiline rows={2} value={form.hero_subtitle ?? ""} onChange={(e) => set("hero_subtitle", e.target.value)} />

          <Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "text.secondary", mb: 0.5 }}>Hero Background Image</Typography>
            <Button variant="outlined" component="label" size="small" sx={{ textTransform: "none" }}>
              {form.hero_background_image instanceof File ? form.hero_background_image.name : "Choose file"}
              <input type="file" hidden accept="image/*" onChange={(e) => set("hero_background_image", e.target.files[0])} />
            </Button>
            {typeof form.hero_background_image === "string" && form.hero_background_image && (
              <Box component="img" src={form.hero_background_image} alt="" sx={{ height: 60, width: "auto", borderRadius: 1, mt: 1, display: "block" }} />
            )}
          </Box>

          <TextField size="small" label="Teaser Text" multiline rows={2} value={form.teaser_text ?? ""} onChange={(e) => set("teaser_text", e.target.value)} />

          <TextField
            size="small" label="Scope Cards (JSON array of { title, description })" multiline rows={6}
            value={form.scope_cards ?? "[]"} onChange={(e) => set("scope_cards", e.target.value)}
            helperText='e.g. [{"title":"Consultancy","description":"..."}]'
          />

          <Typography sx={{ fontWeight: 700, fontSize: 15, mt: 1 }}>SEO</Typography>
          <TextField size="small" label="Meta Title" value={form.meta_title ?? ""} onChange={(e) => set("meta_title", e.target.value)} />
          <TextField size="small" label="Meta Description" multiline rows={2} value={form.meta_description ?? ""} onChange={(e) => set("meta_description", e.target.value)} />

          <Button
            variant="contained"
            sx={{ alignSelf: "flex-start", mt: 1, textTransform: "none", fontWeight: 700, bgcolor: "#2563EB", "&:hover": { bgcolor: "#1D4ED8" } }}
            disabled={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </CardContent>
      </Card>
    </AdminShell>
  );
}